/** The photo report's figures and plots, from one read of the acquisition's `photos.gpkg`. Every viewer sees them (N13). */
import { BarChart, ChartTooltip, LineChart } from '@mantine/charts';
import { SimpleGrid, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { CHART_AXIS_PROPS } from '@/components/charts';
import { StatCard } from '@/components/StatCard';
import { useFormat } from '@/components/useFormat';
import { flightColor, MAP } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { Section } from '../trees/Section';
import type { PhotoPointsConfig } from './config';
import type { PositioningStats } from './positioning';
import { PositioningSection } from './PositioningSection';
import { altitudeKey, altitudeRows, binDigits, HISTOGRAM_METRICS, type HistogramBin, type PhotoStats, type Summary, type TiltExclusion, tiltDigits } from './stats';

const CHART_HEIGHT = 220;

/** A bin's label: its lower edge; a bar of one value (`from` = `to`) as precisely as the value has it. */
const binLabel = (b: HistogramBin, number: (n: number, digits?: number) => string) => number(b.from, b.from === b.to ? 3 : binDigits(b));

/**
 * `tilt`: the photos the photogrammetry leaves out for their tilt (`tiltExclusion`), when the photos have a tilt.
 * `positioning`: where their camera positions come from, when the preprocess run said it.
 */
export function PhotoSections({ stats, config, tilt = null, positioning = null }: { stats: PhotoStats; config: PhotoPointsConfig; tilt?: TiltExclusion | null; positioning?: PositioningStats | null }) {
  const { t } = useTranslation('reports');
  const { number, bytes, dateTime } = useFormat();
  const on = config.sections;
  const o = stats.overview;
  const q = stats.quality;
  const g = stats.georeferencing;
  const med = (s: Summary | null, digits = 0) => (s ? number(s.median, digits) : '—');
  const hasQuality = Object.values(q).some(Boolean) || !!tilt;
  const limits = tilt ? tilt.limits.map((l) => number(l, 1)).join(' / ') : '';
  const limitNote = tilt?.assumed ? t('photos.kpi.tiltLimitAssumed') : '';
  const tiltSub = () => {
    if (!tilt) return undefined;
    if (!tilt.excluded || !tilt.tilts) return t('photos.kpi.tiltExcludedNone', { limit: limits }) + limitNote;
    const d = tiltDigits(tilt.tilts.min, Math.max(...tilt.limits));
    const share = (100 * tilt.excluded) / tilt.total;
    const pct = number(share, share < 1 ? 1 : 0);
    const range =
      tilt.tilts.min === tilt.tilts.max
        ? t('photos.kpi.tiltExcludedOne', { value: number(tilt.tilts.min, d) })
        : t('photos.kpi.tiltExcludedRange', { min: number(tilt.tilts.min, d), max: number(tilt.tilts.max, d), median: number(tilt.tilts.median, d) });
    return t('photos.kpi.tiltExcludedSub', { pct, limit: limits, range }) + limitNote;
  };
  const hasGeo = g.gpsStatus.length > 0 || !!g.errorH || !!g.errorV;
  const metrics = HISTOGRAM_METRICS.filter((m) => stats.histograms[m]);
  const altitudeSeries = stats.altitude.map((f, i) => ({ name: altitudeKey(i), label: f.flight, color: flightColor(i) }));

  return (
    <>
      {on.overview.enabled && (
        <Section title={t('photos.sections.overview')}>
          <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
            <StatCard label={t('photos.kpi.count')} value={number(o.count)} accent testId="photo-stat-count" />
            {o.flights.length > 0 && <StatCard label={t('photos.kpi.flights')} value={number(o.flights.length)} sub={o.flights.join(', ')} />}
            {o.start && <StatCard label={t('photos.kpi.span')} value={dateTime(o.start)} sub={o.end ? dateTime(o.end) : undefined} />}
            {o.flownMinutes !== null && <StatCard label={t('photos.kpi.flown')} value={number(o.flownMinutes, 0)} unit="min" />}
            {o.totalBytes !== null && <StatCard label={t('photos.kpi.size')} value={bytes(o.totalBytes)} />}
            {o.cameras.length > 0 && <StatCard label={t('photos.kpi.cameras')} value={o.cameras.join(', ')} />}
            {o.drones.length > 0 && <StatCard label={t('photos.kpi.drones')} value={o.drones.join(', ')} />}
          </SimpleGrid>
        </Section>
      )}
      {on.quality.enabled && hasQuality && (
        <Section title={t('photos.sections.quality')}>
          <SimpleGrid cols={{ base: 1, xs: 2, md: 5 }} spacing="md">
            {q.agl && <StatCard label={t('photos.kpi.agl')} value={med(q.agl)} unit="m" sub={t('photos.kpi.aglMin', { value: number(q.agl.min) })} />}
            {q.speed && <StatCard label={t('photos.kpi.speed')} value={med(q.speed, 1)} unit="m/s" />}
            {q.forward && <StatCard label={t('photos.kpi.forward')} value={med(q.forward)} unit="%" />}
            {q.side && <StatCard label={t('photos.kpi.side')} value={med(q.side)} unit="%" />}
            {q.tilt && <StatCard label={t('photos.kpi.tilt')} value={med(q.tilt, 1)} unit="°" sub={t('photos.kpi.tiltMax', { value: number(q.tilt.max, 1) })} />}
            {tilt && (
              <StatCard
                label={t('photos.kpi.tiltExcluded')}
                value={t('photos.kpi.tiltExcludedValue', { excluded: number(tilt.excluded), total: number(tilt.total) })}
                sub={tiltSub()}
                testId="photo-stat-tilt-excluded"
              />
            )}
          </SimpleGrid>
        </Section>
      )}
      {on.georeferencing.enabled && hasGeo && (
        <Section title={t('photos.sections.georeferencing')}>
          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="md">
            {g.gpsStatus.length > 0 && (
              <StatCard
                label={t('photos.kpi.gps')}
                value={`${g.gpsStatus[0]!.status} ${number(g.gpsStatus[0]!.pct, 0)} %`}
                sub={g.gpsStatus.slice(1).map((s) => `${s.status} ${number(s.pct, 0)} %`).join(', ') || undefined}
              />
            )}
            {g.errorH && <StatCard label={t('photos.kpi.errorH')} value={med(g.errorH, 2)} unit="m" sub={t('photos.kpi.p95', { value: number(g.errorH.p95, 2) })} />}
            {g.errorV && <StatCard label={t('photos.kpi.errorV')} value={med(g.errorV, 2)} unit="m" sub={t('photos.kpi.p95', { value: number(g.errorV.p95, 2) })} />}
          </SimpleGrid>
        </Section>
      )}
      {on.positioning.enabled && positioning && <PositioningSection stats={positioning} />}
      {on.histograms.enabled && metrics.length > 0 && (
        <Section title={t('photos.sections.histograms')}>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {metrics.map((m) => (
              <Stack key={m} gap={4}>
                <Text fz={13} c={brand.textMuted}>{t(`photos.metrics.${m}`)}</Text>
                {m === 'tilt_deg' && tilt && stats.histograms[m]!.some((b) => b.excluded !== undefined) ? (
                  <TiltHistogram bins={stats.histograms[m]!} limits={limits} />
                ) : (
                  <BarChart
                    h={CHART_HEIGHT}
                    data={stats.histograms[m]!.map((b) => ({ bin: binLabel(b, number), count: b.count }))}
                    dataKey="bin"
                    series={[{ name: 'count', label: t('photos.chart.photos'), color: MAP.photoPoint }]}
                    valueFormatter={(v) => number(v)}
                    {...CHART_AXIS_PROPS}
                    gridAxis="y"
                  />
                )}
              </Stack>
            ))}
          </SimpleGrid>
        </Section>
      )}
      {on.altitude.enabled && stats.altitude.length > 0 && (
        <Section title={t('photos.sections.altitude')}>
          {/* One line per flight, not a scatter: a scatter draws every photo as its own SVG element, and a
              few thousand photos block the page for seconds; a line is one path per flight. */}
          <LineChart
            h={CHART_HEIGHT * 1.5}
            data={altitudeRows(stats.altitude)}
            dataKey="minutes"
            series={altitudeSeries}
            curveType="linear"
            withDots={false}
            xAxisProps={{ type: 'number', domain: ['dataMin', 'dataMax'], tickFormatter: (v: number) => number(v, 0) }}
            xAxisLabel={t('photos.chart.minutes')}
            yAxisLabel={t('photos.chart.agl')}
            withLegend
            valueFormatter={(v) => number(v, 0)}
            tooltipProps={{
              // A row carries one flight's height; the other flights have no photo at that minute.
              content: ({ label, payload }) => (
                <ChartTooltip
                  label={`${number(Number(label), 1)} min`}
                  payload={(payload ?? []).filter((item) => typeof item.value === 'number')}
                  series={altitudeSeries}
                  valueFormatter={(v) => `${number(v, 0)} m`}
                />
              ),
            }}
            {...CHART_AXIS_PROPS}
          />
        </Section>
      )}
    </>
  );
}

/**
 * The tilt histogram split at the limit: the photos the photogrammetry keeps and those it leaves
 * out stacked in two colours, a line where the left-out bars start.
 */
function TiltHistogram({ bins, limits }: { bins: HistogramBin[]; limits: string }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const data = bins.map((b) => ({ bin: binLabel(b, number), kept: b.count - (b.excluded ?? 0), excluded: b.excluded ?? 0 }));
  const first = data.find((d) => d.excluded > 0);
  return (
    <Stack gap={4} data-testid="photo-tilt-histogram">
      <BarChart
        h={CHART_HEIGHT}
        data={data}
        dataKey="bin"
        type="stacked"
        series={[
          { name: 'kept', label: t('photos.chart.tiltKept'), color: MAP.photoPoint },
          { name: 'excluded', label: t('photos.chart.tiltExcluded'), color: MAP.photoExcluded },
        ]}
        referenceLines={first ? [{ x: first.bin, position: 'start', color: MAP.photoExcluded, label: t('photos.chart.tiltLimit', { limit: limits }), labelPosition: 'insideTopLeft' }] : []}
        withLegend
        valueFormatter={(v) => number(v)}
        {...CHART_AXIS_PROPS}
        gridAxis="y"
      />
      <Text fz={12} c={brand.textMuted}>{t('photos.chart.tiltNote', { limit: limits })}</Text>
    </Stack>
  );
}
